<template>
  <div class="wip">
    <!-- PASSWORD GATE -->
    <div class="gate" v-if="!unlocked">
      <h1 class="title">Restricted preview</h1>
      <form class="gate-form" @submit.prevent="unlock">
        <input type="password" v-model="password" placeholder="Password" class="lot-input" autocomplete="off" />
        <div class="safe-btn" @click="unlock">Enter</div>
      </form>
      <div class="no-docs-message" v-if="gateError">Wrong password</div>
    </div>

    <!-- PREVIEW -->
    <div v-else>
      <div class="draft-banner">
        DRAFT &mdash; demonstration page. Lot numbers and documents shown here are test data, not valid for clinical use.
      </div>

      <h1 class="title">Document Download Center</h1>

      <div class="field">
        <input type="text" v-model="lotNumber" placeholder="Lot number" class="lot-input" @keyup.enter="searchDocumentation" />
      </div>
      <div class="field">
        <select v-model="selectedLanguage" class="lang-select">
          <option v-for="lang in languages" :key="lang.value" :value="lang.value">{{ lang.label }}</option>
        </select>
      </div>
      <div class="safe-btn-container">
        <div class="safe-btn" @click="searchDocumentation">Search</div>
      </div>

      <div v-if="loading" class="info-message">Searching...</div>
      <div v-if="apiError" class="no-docs-message">The lot service is unreachable, please try again later.</div>

      <div v-if="searched && !lotFound && !loading && !apiError" class="no-docs-message">
        Lot number not recognized
      </div>

      <template v-if="searched && lotFound">
        <div class="lot-summary">
          Lot <strong>{{ foundLot }}</strong> &middot; range <strong>{{ range.toUpperCase() }}</strong> &middot; regulation <strong>{{ regulatoryStatus }}</strong>
        </div>

        <div v-if="filteredDocumentations.length > 0" class="document-list">
          <div v-for="(doc, index) in filteredDocumentations" :key="index" class="document-item">
            <p>{{ doc.name }}</p>
            <button @click="downloadDocumentation(doc.url)">Download</button>
          </div>
        </div>
        <div v-else class="no-docs-message">No documentation available for this language</div>

        <div class="eifu-container">
          <h2 class="eifu-title">Instructions for use (eIFU)</h2>
          <template v-if="regulatoryStatus === 'MDR'">
            <div v-if="filteredEifuDocumentations.length > 0" class="document-list">
              <div v-for="(doc, index) in filteredEifuDocumentations" :key="index" class="document-item">
                <p>{{ doc.name }}</p>
                <button @click="viewEifu(doc.url)">View</button>
              </div>
            </div>
            <div v-else class="no-docs-message">No eIFU available for this language</div>
          </template>
          <div v-else class="info-message">
            This lot is placed on the market under the former directive (MDD): no electronic instructions for use are provided.
          </div>
        </div>
      </template>

      <div class="demo-lots">
        <div class="demo-title">Demo lot numbers</div>
        <span v-for="lot in demoLots" :key="lot" class="demo-lot" @click="useDemoLot(lot)">{{ lot }}</span>
        <div class="demo-note">These lots exist only after the demo spreadsheet has been imported in the back-office.</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

// SHA-256 of the preview password. This is a courtesy gate for a draft page, NOT security:
// the check runs in the browser, anybody reading the site code can bypass it.
const PASSWORD_HASH = "f1fde3f0424b40d907b53a82d9b5266fb61d2897c8cd9111915ef03a56e5e865";
const STORAGE_KEY = "eifuWipUnlocked";

async function sha256(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
}

export default {
  name : "IfuWip",
  data() {
    return {
      unlocked : false,
      password : "",
      gateError : false,
      lotNumber : "",
      selectedLanguage : "2",
      loading : false,
      apiError : false,
      searched : false,
      lotFound : false,
      foundLot : "",
      range : null,
      regulatoryStatus : null,
      languages : [
        { value : "1", label : "Français" },
        { value : "2", label : "English" }
      ],
      demoLots : ["DEMO-PS-MDR", "DEMO-PS-MDD", "DEMO-VA-MDR", "DEMO-VA-MDD", "DEMO-LC-MDR", "DEMO-LC-MDD", "DEMO-CC-MDR", "DEMO-CC-MDD"],
      documentations : [
        { range : "ps", language : "1", name : "Essential - Technique chirurgicale", url : "/documentation/FR/PSSUP064-FR_A.pdf" },
        { range : "ps", language : "1", name : "Original - Technique chirurgicale", url : "/documentation/FR/PSSUP063-FR_A.pdf" },
        { range : "ps", language : "2", name : "Essential - Surgical technique", url : "/documentation/EN/PSSUP064-EN_B.pdf" },
        { range : "ps", language : "2", name : "Original - Surgical technique", url : "/documentation/EN/PSSUP063-EN_B.pdf" },
        { range : "ps", language : "2", name : "Cypress - Brochure", url : "/documentation/EN/Cypress_PMC003_US_B_02022023.pdf" },
        { range : "ps", language : "2", name : "Oak - Brochure", url : "/documentation/EN/OAK_PMC002_US_B_02022023.pdf" },
        { range : "lc", language : "1", name : "Technique chirurgicale", url : "/documentation/FR/LC-Techop-US FR DE.pdf" },
        { range : "lc", language : "2", name : "Surgical technique", url : "/documentation/EN/LC-Techop-US FR DE.pdf" },
        { range : "lc", language : "2", name : "Cedar & Elm - Brochure", url : "/documentation/EN/Cedar-Elm_PMC004_US_A_02022023.pdf" },
        { range : "cc", language : "1", name : "Technique chirurgicale", url : "/documentation/FR/CC-Techop-US FR DE.pdf" },
        { range : "cc", language : "2", name : "Surgical technique", url : "/documentation/EN/CC-Techop-US FR DE.pdf" },
        { range : "cc", language : "2", name : "Walnut - Brochure", url : "/documentation/EN/Walnut_PMC005_US_A_02022023.pdf" },
        { range : "va", language : "1", name : "Kyphoplasty - Technique chirurgicale", url : "/documentation/FR/PSSUP089-FR_B.pdf" },
        { range : "va", language : "1", name : "Sycamore - Technique chirurgicale", url : "/documentation/FR/VASUP003-1-FR_B.pdf" },
        { range : "va", language : "2", name : "Kyphoplasty - Surgical technique", url : "/documentation/EN/PSSUP089-EN_B.pdf" },
        { range : "va", language : "2", name : "Sycamore - Surgical technique", url : "/documentation/EN/VASUP003-1-EN_B.pdf" }
      ],
      // PLACEHOLDERS: the real eIFU PDFs are not available yet, surgical techniques stand in for them
      eifuDocumentations : [
        { range : "ps", language : "1", name : "Notice d'utilisation (eIFU) - exemple", url : "/documentation/FR/PSSUP064-FR_A.pdf" },
        { range : "ps", language : "2", name : "Instructions for use (eIFU) - sample", url : "/documentation/EN/PSSUP064-EN_B.pdf" },
        { range : "va", language : "1", name : "Notice d'utilisation (eIFU) - exemple", url : "/documentation/FR/PSSUP089-FR_B.pdf" },
        { range : "va", language : "2", name : "Instructions for use (eIFU) - sample", url : "/documentation/EN/PSSUP089-EN_B.pdf" },
        { range : "lc", language : "1", name : "Notice d'utilisation (eIFU) - exemple", url : "/documentation/FR/LC-Techop-US FR DE.pdf" },
        { range : "lc", language : "2", name : "Instructions for use (eIFU) - sample", url : "/documentation/EN/LC-Techop-US FR DE.pdf" },
        { range : "cc", language : "1", name : "Notice d'utilisation (eIFU) - exemple", url : "/documentation/FR/CC-Techop-US FR DE.pdf" },
        { range : "cc", language : "2", name : "Instructions for use (eIFU) - sample", url : "/documentation/EN/CC-Techop-US FR DE.pdf" }
      ],
      filteredDocumentations : [],
      filteredEifuDocumentations : []
    };
  },
  created() {
    try {
      this.unlocked = sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) {
      this.unlocked = false;
    }
    this.selectedLanguage = this.$i18n.locale === "fr" ? "1" : "2";
  },
  mounted() {
    // the page is not meant to be indexed
    this.robots = document.createElement("meta");
    this.robots.name = "robots";
    this.robots.content = "noindex, nofollow";
    document.head.appendChild(this.robots);
  },
  beforeUnmount() {
    if (this.robots) this.robots.remove();
  },
  watch : {
    selectedLanguage() {
      if (this.lotFound) this.filterDocuments();
    }
  },
  methods : {
    async unlock() {
      this.gateError = false;
      let ok = false;
      try {
        ok = (await sha256(this.password)) === PASSWORD_HASH;
      } catch (e) {
        ok = false;
      }
      if (!ok) {
        this.gateError = true;
        return;
      }
      this.unlocked = true;
      this.password = "";
      try {
        sessionStorage.setItem(STORAGE_KEY, "1");
      } catch (e) { /* private mode: the gate simply asks again on reload */ }
    },
    useDemoLot(lot) {
      this.lotNumber = lot;
      this.searchDocumentation();
    },
    searchDocumentation() {
      const lot = this.lotNumber.trim();
      if (!lot || this.loading) return;
      const vm = this;
      this.loading = true;
      this.apiError = false;
      axios
        .get(`${this.$store.state.apiUrl}/public/ifu/lookup/${encodeURIComponent(lot)}`, { validateStatus : () => true })
        .then(function (res) {
          vm.loading = false;
          if (res.status == 200 && res.data && res.data.found) {
            vm.applyLookup(lot.toUpperCase(), res.data.range, res.data.regulatoryStatus);
          } else if (res.status == 200) {
            vm.applyLookup(lot, null, null);
          } else {
            vm.apiError = true;
            vm.searched = false;
          }
        })
        .catch(function () {
          vm.loading = false;
          vm.apiError = true;
          vm.searched = false;
        });
    },
    applyLookup(lot, range, regulatoryStatus) {
      this.searched = true;
      this.foundLot = lot;
      this.range = range;
      this.regulatoryStatus = regulatoryStatus;
      this.lotFound = !!range;
      this.filterDocuments();
    },
    filterDocuments() {
      this.filteredDocumentations = this.documentations.filter(d => d.range === this.range && d.language === this.selectedLanguage);
      this.filteredEifuDocumentations = this.eifuDocumentations.filter(d => d.range === this.range && d.language === this.selectedLanguage);
    },
    downloadDocumentation(url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = "";
      link.click();
    },
    viewEifu(url) {
      window.open(url, "_blank");
    }
  }
};
</script>

<style scoped lang="scss">
.wip {
  min-height: 80vh;
  padding: 120px 0 80px 0;
  color: #fff;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin: 0 0 30px 0;
  text-align: center;
  color: $mainColor;
}

.draft-banner {
  width: 70%;
  max-width: 900px;
  margin: 0 auto 40px auto;
  padding: 12px 20px;
  box-sizing: border-box;
  text-align: center;
  font-size: 14px;
  letter-spacing: 0.5px;
  color: #ffcc00;
  border: 1px solid #ffcc00;
}

.gate {
  padding-top: 60px;
}

.gate-form, .field {
  display: flex;
  justify-content: center;
  padding: 0 20%;
  padding-bottom: 20px;
  box-sizing: border-box;
}

.gate-form {
  gap: 15px;
  align-items: flex-start;
}

.lot-input, .lang-select {
  background: #000;
  border: 1px solid $mainColor;
  color: $mainColor;
  padding: 0 20px;
  width: 100%;
  height: 40px;
  font-size: 20px;
  box-sizing: border-box;
}

.safe-btn-container {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
}

.safe-btn {
  flex: none;
  color: $mainColor;
  border: 1px solid $mainColor;
  font-size: 20px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: 0.5s;
  width: 100px;
}

.safe-btn:hover {
  background: $mainColor;
  color: #fff;
}

.no-docs-message {
  font-size: 18px;
  text-align: center;
  color: #ff0505;
  margin-top: 20px;
}

.info-message {
  font-size: 16px;
  text-align: center;
  color: #ccc;
  margin-top: 20px;
  padding: 0 10%;
}

.lot-summary {
  text-align: center;
  margin-top: 30px;
  font-size: 16px;
  color: #ccc;
}

.lot-summary strong {
  color: #fff;
}

.document-list {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.document-item {
  margin-bottom: 10px;
  text-align: center;
  padding-bottom: 3%;
}

.document-item button {
  background-color: $mainColor;
  color: white;
  font-size: 20px;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.document-item button:hover {
  background-color: #5a8c01;
}

.document-item p {
  font-size: 25px;
}

.eifu-container {
  margin: 40px 10% 0 10%;
  padding-top: 20px;
  border-top: 1px solid #333;
}

.eifu-title {
  text-align: center;
  color: #fff;
  font-weight: 300;
  font-size: 22px;
  text-transform: uppercase;
}

.demo-lots {
  margin: 70px auto 0 auto;
  width: 70%;
  max-width: 900px;
  text-align: center;
  color: #888;
  font-size: 13px;
}

.demo-title {
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
}

.demo-lot {
  display: inline-block;
  margin: 4px;
  padding: 4px 10px;
  border: 1px solid #444;
  color: #bbb;
  cursor: pointer;
  transition: 0.3s;
}

.demo-lot:hover {
  border-color: $mainColor;
  color: $mainColor;
}

.demo-note {
  margin-top: 12px;
  font-style: italic;
}

@media screen and (max-width: 768px) {
  .wip {
    padding-top: 90px;
  }

  .draft-banner {
    width: 90%;
  }

  .gate-form, .field {
    padding: 0 8% 20px 8%;
  }

  .document-item p {
    font-size: 18px;
  }

  .eifu-container {
    margin: 40px 6% 0 6%;
  }

  .demo-lots {
    width: 90%;
  }
}
</style>
